// Schéma des 8 étapes de l'onboarding client (module Acquisition).
// Chaque champ : { name, label, type, placeholder?, options?, hint?, showIf?, required? }

export const ONBOARDING_STEPS = [
  {
    key: "entreprise",
    title: "Votre entreprise",
    intro: "Commençons par les bases : qui êtes-vous et où travaillez-vous ?",
    fields: [
      { name: "company", label: "Nom de l'entreprise", type: "text", required: true },
      { name: "website", label: "Site web", type: "text", placeholder: "https://" },
      { name: "activity", label: "Votre activité en une ligne", type: "text", required: true },
      { name: "activity_description", label: "Décrivez votre activité", type: "textarea" },
      { name: "area", label: "Zone commerciale", type: "text", placeholder: "Villes, départements, rayon en km", required: true },
      { name: "years", label: "Années d'existence", type: "number" },
      { name: "size", label: "Taille de l'entreprise", type: "select", options: ["1 personne", "2 à 5", "6 à 10", "11 à 50", "50+"] },
      { name: "contact_phone", label: "Téléphone de contact", type: "text" },
      { name: "contact_email", label: "Email de contact", type: "text" },
    ],
  },
  {
    key: "offre",
    title: "Votre offre",
    intro: "Ce que nous allons mettre en avant dans les campagnes.",
    fields: [
      { name: "product", label: "Produit ou service à promouvoir", type: "text", required: true },
      { name: "product_description", label: "Description", type: "textarea" },
      { name: "price", label: "Prix / panier moyen (€)", type: "number" },
      { name: "margin", label: "Marge approximative (%)", type: "number" },
      { name: "promo", label: "Offre promotionnelle en cours", type: "textarea", hint: "Laissez vide si vous n'en avez pas — nous n'inventerons rien." },
      { name: "advantages", label: "Vos avantages concurrentiels", type: "list", hint: "Un avantage par ligne" },
      { name: "guarantees", label: "Garanties réellement proposées", type: "list" },
      { name: "delays", label: "Délais habituels", type: "text" },
      { name: "priority_services", label: "Prestations prioritaires", type: "list" },
    ],
  },
  {
    key: "clients",
    title: "Vos clients",
    intro: "À qui parlons-nous ?",
    fields: [
      { name: "market", label: "Vous vendez à", type: "radio", options: ["B2B", "B2C", "Les deux"], required: true },
      { name: "ideal_client", label: "Votre client idéal", type: "textarea", required: true },
      { name: "profile", label: "Profil type", type: "textarea", hint: "Secteur, taille, rôle du décideur… ou profil particulier si B2C" },
      { name: "pains", label: "Leurs problématiques", type: "list" },
      { name: "motivations", label: "Leurs motivations d'achat", type: "list" },
      { name: "objections", label: "Leurs objections fréquentes", type: "list" },
      { name: "qualify", label: "Critères de qualification", type: "list", hint: "Qu'est-ce qu'un bon lead pour vous ?" },
      { name: "disqualify", label: "Critères de disqualification", type: "list" },
    ],
  },
  {
    key: "acquisition",
    title: "Votre acquisition",
    intro: "Votre historique publicitaire et vos objectifs.",
    fields: [
      { name: "google_used", label: "Avez-vous déjà utilisé Google Ads ?", type: "radio", options: ["Non", "Oui"], required: true },
      { name: "google_budget", label: "Budget Google Ads passé (€/mois)", type: "number", showIf: (d) => d.google_used === "Oui" },
      { name: "google_history", label: "Historique Google Ads", type: "textarea", showIf: (d) => d.google_used === "Oui" },
      { name: "google_cpl", label: "CPL connu sur Google (€)", type: "number", showIf: (d) => d.google_used === "Oui" },
      { name: "google_results", label: "Résultats obtenus", type: "textarea", showIf: (d) => d.google_used === "Oui" },
      { name: "google_account", label: "Compte Google Ads existant (ID ou email)", type: "text", showIf: (d) => d.google_used === "Oui" },

      { name: "meta_used", label: "Avez-vous déjà utilisé Meta Ads ?", type: "radio", options: ["Non", "Oui"], required: true },
      { name: "meta_budget", label: "Budget Meta passé (€/mois)", type: "number", showIf: (d) => d.meta_used === "Oui" },
      { name: "meta_history", label: "Historique Meta Ads", type: "textarea", showIf: (d) => d.meta_used === "Oui" },
      { name: "meta_cpl", label: "CPL connu sur Meta (€)", type: "number", showIf: (d) => d.meta_used === "Oui" },
      { name: "meta_results", label: "Résultats obtenus", type: "textarea", showIf: (d) => d.meta_used === "Oui" },
      { name: "meta_account", label: "Compte publicitaire Meta existant", type: "text", showIf: (d) => d.meta_used === "Oui" },

      { name: "monthly_budget", label: "Budget publicitaire mensuel souhaité (€)", type: "number", required: true },
      { name: "goal_leads", label: "Objectif leads / mois", type: "number" },
      { name: "goal_meetings", label: "Objectif RDV / mois", type: "number" },
      { name: "goal_sales", label: "Objectif ventes / mois", type: "number" },
      { name: "goal_revenue", label: "CA objectif / mois (€)", type: "number" },
      { name: "cac", label: "Coût d'acquisition client connu (€)", type: "number" },
      { name: "closing_rate", label: "Taux de closing connu (%)", type: "number" },
    ],
  },
  {
    key: "concurrents",
    title: "Vos concurrents",
    intro: "Ceux que vous croisez le plus souvent.",
    fields: [
      {
        name: "competitors", label: "Concurrents", type: "repeater",
        itemFields: [
          { name: "name", label: "Nom", type: "text" },
          { name: "url", label: "Site web", type: "text" },
          { name: "note", label: "Remarque", type: "text" },
        ],
      },
    ],
  },
  {
    key: "marque",
    title: "Votre marque",
    intro: "Vos éléments visuels. Formats acceptés : images, PDF, vidéos courtes, archives (25 Mo max par fichier).",
    fields: [
      { name: "upload_logo", label: "Logo", type: "upload", kind: "logo" },
      { name: "upload_charte", label: "Charte graphique", type: "upload", kind: "charte" },
      { name: "upload_photos", label: "Photos", type: "upload", kind: "photo", multiple: true },
      { name: "upload_videos", label: "Vidéos", type: "upload", kind: "video", multiple: true },
      { name: "upload_examples", label: "Exemples de créations", type: "upload", kind: "exemple", multiple: true },
      { name: "upload_docs", label: "Autres documents", type: "upload", kind: "document", multiple: true },
      { name: "colors", label: "Couleurs de la marque", type: "text", placeholder: "#1A1A1A, #C8A96A…" },
      { name: "fonts", label: "Typographies", type: "text" },
      { name: "graphic_rules", label: "Règles graphiques à respecter", type: "textarea" },
    ],
  },
  {
    key: "communication",
    title: "Votre communication",
    intro: "Pour écrire comme vous, pas comme un robot.",
    fields: [
      { name: "tone", label: "Ton souhaité", type: "select", options: ["Professionnel", "Chaleureux", "Direct", "Expert", "Décontracté"] },
      { name: "address", label: "Tutoiement ou vouvoiement ?", type: "radio", options: ["Tutoiement", "Vouvoiement"] },
      { name: "language_level", label: "Niveau de langage", type: "select", options: ["Simple", "Courant", "Technique"] },
      { name: "style", label: "Style rédactionnel", type: "textarea" },
      { name: "emojis", label: "Emojis", type: "radio", options: ["Jamais", "Avec parcimonie", "Oui"] },
      { name: "hashtags", label: "Hashtags habituels", type: "text" },
      { name: "ctas", label: "CTA habituels", type: "list" },
      { name: "forbidden_topics", label: "Sujets interdits", type: "list" },
      { name: "forbidden_words", label: "Termes interdits", type: "list" },
      { name: "forbidden_promises", label: "Promesses interdites", type: "list" },
      { name: "liked_examples", label: "Exemples de textes que vous aimez", type: "textarea" },
    ],
  },
  {
    key: "comptes",
    title: "Comptes & réseaux",
    intro: "Vos pages publiques. Les connexions sécurisées (OAuth) se feront après l'onboarding — ne nous envoyez jamais vos mots de passe.",
    fields: [
      { name: "instagram", label: "Instagram", type: "text", placeholder: "@votrecompte ou URL" },
      { name: "facebook", label: "Facebook", type: "text" },
      { name: "linkedin", label: "LinkedIn", type: "text" },
      { name: "google_business", label: "Fiche Google Business Profile", type: "text" },
      { name: "landing_url", label: "Page de destination des campagnes", type: "text", placeholder: "https://" },
    ],
  },
];
